import type {
  ArticleSuggestion,
  CallSection,
  ChatSuggestion,
  LiveCoachingEvent,
  Nudge,
} from "@repo/app-types/entities/liveCoaching";
import {
  RecordingSpeakerRoleType,
  type TranscriptHighlight,
} from "@repo/app-types/entities/recording";
import type {
  AccountGradingRecording,
  GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { MessageItem } from "@repo/ui/chat/messageItem";
import { MessageTimeline } from "@repo/ui/chat/messageTimeline";
import { Size } from "@repo/ui/helpers/helpers";
import { ChatComposer } from "@repo/ui/liveCoaching/chatComposer";
import {
  LiveCoachingWidget,
  type CallStatus,
} from "@repo/ui/liveCoaching/liveCoachingWidget";
import type {
  Utterance,
  UtteranceHighlight,
} from "@repo/ui/liveCoaching/transcriptContent";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type FC,
} from "react";
import { twMerge } from "tailwind-merge";
import { useLazyKnowledgeBaseAttachmentByIdQuery } from "../../../api/entities/knowledgeBase.js";
import { useAudioPlayer } from "../../../components/audioPlayer/hook.js";
import { useChildWindow } from "../../../components/childWindow.js";
import { useLiveCoachingCopilot } from "../useLiveCoachingCopilot.js";
import { LiveCoachingMessageContent } from "./liveCoachingMessageContent.js";

export interface LiveCoachingPreviewWindowProps {
  gradingId: GradingId;
  sortedRecordings: readonly AccountGradingRecording[];
}

export function useLiveCoachingPreviewWindow(
  props: LiveCoachingPreviewWindowProps,
) {
  const { open, render } = useChildWindow({
    width: 440,
    height: 720,
  });

  return {
    liveCoachingPreviewWindow: render(
      <LiveCoachingPreviewWindowContent {...props} />,
    ),
    onLiveCoachingPreviewOpen: open,
  };
}

export const LiveCoachingPreviewWindowContent: FC<
  LiveCoachingPreviewWindowProps
> = (props) => {
  const { currentSourceKey, nativeAudio } = useAudioPlayer();
  const [fetchAttachmentByIdWithContent] =
    useLazyKnowledgeBaseAttachmentByIdQuery();

  // Play/pause when the live coaching preview opens/closes
  useEffect(() => {
    nativeAudio?.play();

    return () => {
      nativeAudio?.pause();
    };
  }, [nativeAudio]);

  const callTimeSeconds = useCallTimeSeconds(nativeAudio);
  const callDurationSeconds = useCallDurationSeconds(nativeAudio);

  const recording = props.sortedRecordings.find(
    (r) => r.id === currentSourceKey,
  );

  const speakersById = useMemo(
    () =>
      recording?.speakers
        ? new Map(recording.speakers.map((s) => [s.id, s]))
        : undefined,
    [recording?.speakers],
  );

  const liveCoachingContent = recording?.live_coaching_content;
  const transcriptHighlights = liveCoachingContent?.transcriptHighlights;

  const transcriptUtterances = useMemo(() => {
    const utterances: Utterance[] = [];

    for (const fragment of recording?.transcript?.fragments ?? []) {
      const speaker = speakersById?.get(fragment.speaker_id);

      const role =
        speaker?.role_type === RecordingSpeakerRoleType.Representative
          ? "agent"
          : "customer";

      const startTimeSeconds = fragment.start_time / 1000;
      const endTimeSeconds = fragment.end_time / 1000;

      // Full fragment
      if (
        endTimeSeconds <= callTimeSeconds ||
        Math.floor(callDurationSeconds) <= callTimeSeconds
      ) {
        utterances.push({
          role,
          text: fragment.text_transcript,
          startTimeSeconds,
          endTimeSeconds,
          highlights: findUtteranceHighlights(
            fragment.text_transcript,
            transcriptHighlights,
          ),
        });
        continue;
      }

      if (startTimeSeconds <= callTimeSeconds) {
        const simulatedEndTimeSeconds = callTimeSeconds;
        const simulatedDuration = simulatedEndTimeSeconds - startTimeSeconds;

        const totalDurationSeconds = endTimeSeconds - startTimeSeconds;

        const allWords = fragment.text_transcript.split(/[ ]+/);
        const simulatedEndWordIndex = Math.ceil(
          allWords.length * (simulatedDuration / totalDurationSeconds),
        );

        const simulatedText = allWords
          .slice(0, simulatedEndWordIndex)
          .join(" ");
        utterances.push({
          role,
          text: simulatedText,
          startTimeSeconds,
          endTimeSeconds: simulatedEndTimeSeconds,
          highlights: findUtteranceHighlights(
            simulatedText,
            transcriptHighlights,
          ),
        });
        continue;
      }

      break;
    }

    return utterances;
  }, [
    recording?.transcript,
    speakersById,
    callTimeSeconds,
    callDurationSeconds,
    transcriptHighlights,
  ]);

  const summary = liveCoachingContent?.summary;

  const { activeNudge, callSections, articleSuggestions, chatSuggestions } =
    useLiveCoachingState(
      liveCoachingContent?.initialSections,
      liveCoachingContent?.events,
      callTimeSeconds,
    );

  const {
    visibleMessages,
    isLoading,
    composerValue,
    setComposerValue,
    onMessageSend,
  } = useLiveCoachingCopilot({
    gradingId: props.gradingId,
    recordingId: recording?.id,
    callTimeSeconds,
  });

  return (
    <LiveCoachingWidget
      callStatus={computeCallStatus(callTimeSeconds, callDurationSeconds)}
      callTimeSeconds={callTimeSeconds}
      activeNudge={activeNudge}
      callSections={callSections}
      articleSuggestions={articleSuggestions}
      onArticleSuggestionOpen={async (suggestion, signal) => {
        const promise = fetchAttachmentByIdWithContent({
          attachmentId: suggestion.attachmentId,
        });
        signal.addEventListener("abort", () => promise.abort());
        const attachment = await promise.unwrap();
        return { body: attachment.body ?? "This article is empty." };
      }}
      chatSuggestions={chatSuggestions}
      onChatSuggestionClick={(suggestion) =>
        onMessageSend(suggestion.messageText)
      }
      chatMessagesCount={visibleMessages.length}
      renderChatMessagesContainer={({ className }) => (
        <MessageTimeline
          className={twMerge(className)}
          messages={visibleMessages}
        >
          {(message) => (
            <MessageItem
              size={Size.SMALL}
              role={message.role}
              prose
              isSaving={message.role === "user" && message.isSaving}
              isError={message.isError}
            >
              <LiveCoachingMessageContent
                content={message.content}
                isStreaming={
                  message.role === "assistant" && message.isLoading === true
                }
              />
            </MessageItem>
          )}
        </MessageTimeline>
      )}
      chatComposer={
        <ChatComposer
          text={composerValue}
          onTextChange={setComposerValue}
          canSend={!isLoading}
          onSend={() => onMessageSend(composerValue)}
        />
      }
      transcriptUtterances={transcriptUtterances}
      coachingTips={summary?.coachingTips}
      summaryMarkdown={summary?.summaryMarkdown}
      nextSteps={summary?.nextSteps}
      metaFields={summary?.metaFields}
    />
  );
};

function findUtteranceHighlights(
  text: string,
  transcriptHighlights: readonly TranscriptHighlight[] | undefined,
): UtteranceHighlight[] {
  if (!transcriptHighlights || transcriptHighlights.length === 0) {
    return [];
  }

  const highlights: UtteranceHighlight[] = [];
  for (const { text: needle } of transcriptHighlights) {
    if (!needle) continue;
    let cursor = 0;
    while (cursor < text.length) {
      const startIndex = text.indexOf(needle, cursor);
      if (startIndex === -1) break;
      const endIndex = startIndex + needle.length;
      highlights.push({ startIndex, endIndex, text: needle });
      cursor = endIndex;
    }
  }
  return highlights;
}

function roundCallTime(callTimeSeconds: number) {
  // Round to the nearest 250 ms to avoid excessive updates
  return Math.round(callTimeSeconds / 0.25) * 0.25;
}

function useCallTimeSeconds(nativeAudio: HTMLAudioElement | null) {
  const callTimeSecondsRef = useRef(0);
  const callTimeSeconds = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        const onTimeChange = () => {
          // We have to capture the value at the time of the event, because `useSyncExternalStore` requires `getSnapshot` to be stable but Safari returns a different value for `currentTime` every time
          callTimeSecondsRef.current = roundCallTime(
            nativeAudio?.currentTime ?? 0,
          );
          onStoreChange();
        };

        nativeAudio?.addEventListener("emptied", onTimeChange);
        nativeAudio?.addEventListener("timeupdate", onTimeChange);

        onTimeChange();

        return () => {
          nativeAudio?.removeEventListener("emptied", onTimeChange);
          nativeAudio?.removeEventListener("timeupdate", onTimeChange);
        };
      },
      [nativeAudio],
    ),
    () => callTimeSecondsRef.current,
  );
  return callTimeSeconds;
}

function useCallDurationSeconds(nativeAudio: HTMLAudioElement | null) {
  return useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) => {
        nativeAudio?.addEventListener("emptied", onStoreChange);
        nativeAudio?.addEventListener("durationchange", onStoreChange);

        return () => {
          nativeAudio?.removeEventListener("emptied", onStoreChange);
          nativeAudio?.removeEventListener("durationchange", onStoreChange);
        };
      },
      [nativeAudio],
    ),
    () => roundCallTime(nativeAudio?.duration ?? 0),
  );
}

interface LiveCoachingState {
  activeNudge: Nudge | undefined;
  callSections: readonly CallSection[];
  articleSuggestions: readonly ArticleSuggestion[];
  chatSuggestions: readonly ChatSuggestion[];
}

function useLiveCoachingState(
  initialSections: readonly CallSection[] | undefined,
  events: readonly LiveCoachingEvent[] | undefined,
  callTimeSeconds: number,
): LiveCoachingState {
  return useMemo(() => {
    const callTimeMillis = callTimeSeconds * 1000;
    const activeNudges = new Map<string, Nudge>();
    const activeArticleSuggestions = new Map<string, ArticleSuggestion>();
    const activeChatSuggestions = new Map<string, ChatSuggestion>();
    const callSectionsById = new Map<string, CallSection>(
      (initialSections ?? []).map((section) => [section.id, { ...section }]),
    );

    for (const event of events ?? []) {
      if (event.timeMillis > callTimeMillis) break;

      switch (event.type) {
        case "nudge_entered":
          activeNudges.set(event.nudge.id, event.nudge);
          break;
        case "nudge_exited":
          activeNudges.delete(event.nudgeId);
          break;
        case "call_section_updated": {
          const section = callSectionsById.get(event.sectionId);
          if (section) {
            callSectionsById.set(event.sectionId, {
              ...section,
              status: event.status,
            });
          }
          break;
        }
        case "article_suggestion_entered":
          activeArticleSuggestions.set(event.suggestion.id, event.suggestion);
          break;
        case "article_suggestion_exited":
          activeArticleSuggestions.delete(event.suggestionId);
          break;
        case "chat_suggestion_entered":
          activeChatSuggestions.set(event.suggestion.id, event.suggestion);
          break;
        case "chat_suggestion_exited":
          activeChatSuggestions.delete(event.suggestionId);
          break;
      }
    }

    // The most recently entered nudge wins as the single active nudge
    const nudgeValues = Array.from(activeNudges.values());
    const activeNudge = nudgeValues[nudgeValues.length - 1];

    const callSections = (initialSections ?? []).map(
      (section) => callSectionsById.get(section.id) ?? section,
    );

    return {
      activeNudge,
      callSections,
      articleSuggestions: Array.from(activeArticleSuggestions.values()),
      chatSuggestions: Array.from(activeChatSuggestions.values()),
    };
  }, [initialSections, events, callTimeSeconds]);
}

function computeCallStatus(
  callTimeSeconds: number,
  callDurationSeconds: number,
): CallStatus {
  if (callTimeSeconds < 1) {
    return "idle";
  }

  if (callTimeSeconds < Math.floor(callDurationSeconds)) {
    return "started";
  }

  return "ended";
}
